import type React from "react";
import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, X, Clock, ImagePlus, Sparkles } from "lucide-react";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogClose } from "../../ui/dialog";
import {
  createGlamPackage,
  updateGlamPackage,
  getAllGlamPackages,
  deleteGlamPackage,
  getGlamPackage,
} from "../../../services/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { cn } from "../../../lib/util";

interface Duration {
  id?: string;
  hours: number;
  minutes: number;
  glam_package_id?: string;
}

interface Category {
  media_url: string;
  id: string;
  title: string;
  price: number;
  description?: string;
  duration?: Duration | null;
}

const ctrl =
  "w-full rounded-xl border border-[#E3C9C8] bg-white px-4 py-3 text-[15px] text-[#3a2e2c] " +
  "placeholder:text-[#bcaca9] outline-none transition-colors " +
  "focus-visible:ring-2 focus-visible:ring-[#B87775]/50 focus-visible:border-[#B87775]";

const HOURS_MAX = 12;
const MINUTE_STEPS = [0, 15, 30, 45];

function formatDuration(d?: Duration | null): string | null {
  if (!d) return null;
  const h = Number(d.hours) || 0;
  const m = Number(d.minutes) || 0;
  if (!h && !m) return null;
  const parts: string[] = [];
  if (h) parts.push(`${h} hr${h > 1 ? "s" : ""}`);
  if (m) parts.push(`${m} min`);
  return parts.join(" ");
}

export function BookingCategory() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryPrice, setCategoryPrice] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);
  const [durationService, setDurationService] = useState<Category | null>(null);
  const [duration, setDuration] = useState<Duration>({ hours: 1, minutes: 0 });
  const [isDurationLoading, setIsDurationLoading] = useState(false);
  const [isSavingDuration, setIsSavingDuration] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGlamPackages = async () => {
      try {
        const response = (await getAllGlamPackages()) as any;
        setCategories(response.data.data);
      } catch (error) {
        console.error("Failed to fetch glam packages:", error);
        toast.error("Failed to fetch services.", { position: "top-center", autoClose: 3000 });
      } finally {
        setIsLoading(false);
      }
    };
    fetchGlamPackages();
  }, []);

  // Deep link: ⌘K "New service" → /control/services?new=1 opens the add modal.
  useEffect(() => {
    if (params.get("new") === "1") {
      handleOpenModal();
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, []); // run once on mount

  const handleOpenDurationModal = async (service: Category) => {
    setDurationService(service);
    setDuration(
      service.duration
        ? { hours: service.duration.hours ?? 0, minutes: service.duration.minutes ?? 0 }
        : { hours: 1, minutes: 0 }
    );
    setIsDurationModalOpen(true);
    setIsDurationLoading(true);
    try {
      const response = (await getGlamPackage(service.id)) as any;
      const d = response.data?.data?.duration;
      if (d) setDuration({ hours: d.hours ?? 0, minutes: d.minutes ?? 0 });
    } catch (error) {
      // Non-fatal — we already seeded from the list value.
      console.error("Failed to fetch duration:", error);
    } finally {
      setIsDurationLoading(false);
    }
  };

  const handleSaveDuration = async () => {
    if (!durationService) return;
    try {
      setIsSavingDuration(true);
      const formData = new FormData();
      formData.append("duration[hours]", duration.hours.toString());
      formData.append("duration[minutes]", duration.minutes.toString());
      await updateGlamPackage(durationService.id, formData);

      setCategories((prev) =>
        prev.map((c) =>
          c.id === durationService.id
            ? { ...c, duration: { hours: duration.hours, minutes: duration.minutes } }
            : c
        )
      );
      toast.success("Duration updated.", { position: "top-center", autoClose: 3000 });
      setIsDurationModalOpen(false);
    } catch (error) {
      console.error("Failed to save duration:", error);
      toast.error("Failed to save duration.", { position: "top-center", autoClose: 3000 });
    } finally {
      setIsSavingDuration(false);
    }
  };

  const handleOpenModal = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setCategoryName(category.title);
      setCategoryPrice(category.price.toString());
      setCategoryDescription(category.description || "");
      setThumbnailPreview(category.media_url);
    } else {
      setEditingCategory(null);
      setCategoryName("");
      setCategoryPrice("");
      setCategoryDescription("");
      setThumbnailPreview(null);
    }
    setIsModalOpen(true);
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) setThumbnailPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!categoryName.trim() || !categoryPrice.trim() || !thumbnailPreview) {
      toast.error("Please fill in all required fields.", { position: "top-center", autoClose: 3000 });
      return;
    }
    const price = Number.parseFloat(categoryPrice);
    if (isNaN(price)) {
      toast.error("Please enter a valid price.", { position: "top-center", autoClose: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append("title", categoryName.trim());
    formData.append("price", price.toString());
    formData.append("description", categoryDescription.trim());
    if (fileInputRef.current?.files?.[0]) formData.append("image", fileInputRef.current.files[0]);

    try {
      if (editingCategory) {
        await updateGlamPackage(editingCategory.id, formData);
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === editingCategory.id
              ? {
                  ...cat,
                  title: categoryName.trim(),
                  price,
                  description: categoryDescription.trim(),
                  media_url: thumbnailPreview!,
                }
              : cat
          )
        );
        toast.success("Service updated.", { position: "top-center", autoClose: 3000 });
      } else {
        await createGlamPackage(formData);
        setCategories((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            title: categoryName.trim(),
            price,
            description: categoryDescription.trim(),
            media_url: thumbnailPreview!,
          },
        ]);
        toast.success("Service added.", { position: "top-center", autoClose: 3000 });
      }
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error:", error);
      toast.error(editingCategory ? "Failed to update service." : "Failed to add service.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteGlamPackage(id);
      setCategories((prev) => prev.filter((cat) => cat.id !== id));
      toast.success("Service deleted.", { position: "top-center", autoClose: 3000 });
    } catch (error) {
      console.error("Failed to delete category:", error);
      toast.error("Failed to delete service.", { position: "top-center", autoClose: 3000 });
    }
  };

  const resetForm = () => {
    setCategoryName("");
    setCategoryPrice("");
    setCategoryDescription("");
    setThumbnailPreview(null);
    setEditingCategory(null);
  };

  return (
    <div className="mx-auto max-w-4xl">
      <ToastContainer />

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">Catalogue</p>
          <h1 className="font-serif text-3xl text-[#3a2e2c]">Services</h1>
          <p className="mt-1 text-sm text-[#8a7a78]">
            Your glam packages — pricing, descriptions, and the session duration each one
            needs for scheduling.
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleOpenModal()}
          className="flex h-11 items-center gap-2 rounded-full bg-[#B87775] px-5 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
        >
          <Plus className="h-4 w-4" />
          Add service
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 py-20 text-[#9A6B69]">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#EAD7D6] border-t-[#B87775]" />
          Loading services…
        </div>
      ) : categories.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E3C9C8] bg-white px-6 py-16 text-center">
          <Sparkles className="mx-auto h-10 w-10 text-[#D9C4C3]" />
          <p className="mt-3 font-serif text-xl text-[#3a2e2c]">No services yet</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-[#8a7a78]">
            Add your first glam package so clients can book it.
          </p>
          <button
            type="button"
            onClick={() => handleOpenModal()}
            className="mt-4 rounded-full bg-[#B87775] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#a96764]"
          >
            Add service
          </button>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {categories.map((category) => {
            const durationLabel = formatDuration(category.duration);
            return (
              <li
                key={category.id}
                className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#EAD7D6] bg-white p-4 transition hover:border-[#B87775]/60"
              >
                <img
                  src={category.media_url || "/placeholder.svg"}
                  alt={category.title}
                  className="h-16 w-16 shrink-0 rounded-xl object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-lg text-[#3a2e2c]">{category.title}</p>
                  <p className="text-sm tabular-nums text-[#8a5a58]">${category.price}</p>
                </div>

                {/* Duration — visible & one click to edit */}
                <button
                  type="button"
                  onClick={() => handleOpenDurationModal(category)}
                  className={cn(
                    "inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]",
                    durationLabel
                      ? "border-[#EAD7D6] bg-[#FBEFEF] text-[#8a5a58] hover:border-[#B87775]"
                      : "border-dashed border-[#E3C9C8] text-[#B87775] hover:bg-[#FBEFEF]"
                  )}
                >
                  <Clock className="h-4 w-4 text-[#B87775]" />
                  <span className="tabular-nums">{durationLabel ?? "Set duration"}</span>
                </button>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleOpenModal(category)}
                    aria-label={`Edit ${category.title}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-[#B87775] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteCategory(category.id)}
                    aria-label={`Delete ${category.title}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-[#c0504d] transition hover:bg-[#fbeceb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c0504d]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Add / edit service ───────────────────────────────────────── */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="overflow-hidden rounded-2xl border-[#EAD7D6] p-0 sm:max-w-[860px]">
          <DialogClose className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-[#B87775] transition hover:bg-[#FBEFEF] focus:outline-none focus:ring-2 focus:ring-[#B87775]">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>

          <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
            <div className="flex h-[360px] flex-col items-center justify-center rounded-xl border border-[#EAD7D6] bg-[#FBEFEF]/40 p-4">
              {thumbnailPreview ? (
                <div className="relative flex h-full w-full flex-col items-center">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    className="h-full w-full rounded-lg object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-3 rounded-full bg-[#B87775] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#a96764]"
                  >
                    Change photo
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center gap-3 text-[#9A6B69]"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white">
                    <ImagePlus className="h-7 w-7 text-[#B87775]" />
                  </span>
                  <span className="rounded-full bg-[#B87775] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#a96764]">
                    Upload thumbnail
                  </span>
                  <span className="text-xs">JPG or PNG</span>
                </button>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleThumbnailChange}
                accept="image/*"
                className="hidden"
              />
            </div>

            <div className="flex flex-col">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">
                {editingCategory ? "Edit service" : "New service"}
              </p>
              <h2 className="mb-4 font-serif text-2xl text-[#3a2e2c]">
                {editingCategory ? categoryName || "Service" : "Add a service"}
              </h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="categoryName" className="mb-1.5 block text-[13px] font-medium text-[#8a5a58]">
                    Service name <span className="text-[#B87775]">*</span>
                  </label>
                  <input
                    id="categoryName"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    placeholder="e.g. Bridal Glam"
                    className={ctrl}
                  />
                </div>
                <div>
                  <label htmlFor="categoryPrice" className="mb-1.5 block text-[13px] font-medium text-[#8a5a58]">
                    Price ($) <span className="text-[#B87775]">*</span>
                  </label>
                  <input
                    id="categoryPrice"
                    type="number"
                    value={categoryPrice}
                    onChange={(e) => setCategoryPrice(e.target.value)}
                    placeholder="Enter price"
                    className={cn(ctrl, "tabular-nums")}
                  />
                </div>
                <div>
                  <label htmlFor="categoryDescription" className="mb-1.5 block text-[13px] font-medium text-[#8a5a58]">
                    Description <span className="text-[#B87775]">*</span>
                  </label>
                  <Textarea
                    id="categoryDescription"
                    value={categoryDescription}
                    onChange={(e) => setCategoryDescription(e.target.value)}
                    placeholder="A short, inviting description…"
                    className={cn(ctrl, "min-h-[120px] resize-none")}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsModalOpen(false);
                  }}
                  className="inline-flex h-11 items-center rounded-full border border-[#E3C9C8] px-5 text-sm font-medium text-[#8a5a58] transition hover:bg-[#FBEFEF]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!categoryName.trim() || !categoryPrice.trim() || !thumbnailPreview}
                  className="inline-flex h-11 items-center rounded-full bg-[#B87775] px-6 text-sm font-medium text-white transition hover:bg-[#a96764] disabled:opacity-50"
                >
                  {editingCategory ? "Save changes" : "Add service"}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Session duration ─────────────────────────────────────────── */}
      <Dialog open={isDurationModalOpen} onOpenChange={setIsDurationModalOpen}>
        <DialogContent className="max-w-md rounded-2xl border-[#EAD7D6]">
          <DialogClose className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-[#B87775] transition hover:bg-[#FBEFEF] focus:outline-none focus:ring-2 focus:ring-[#B87775]">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>

          <div>
            <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-[#FBEFEF]">
              <Clock className="h-5 w-5 text-[#B87775]" />
            </div>
            <h2 className="font-serif text-2xl text-[#3a2e2c]">Session duration</h2>
            <p className="mt-0.5 text-sm text-[#8a7a78]">
              How long does{" "}
              <span className="font-medium text-[#3a2e2c]">
                {durationService?.title ?? "this service"}
              </span>{" "}
              take? This drives how slots are offered.
            </p>
          </div>

          {isDurationLoading ? (
            <div className="flex items-center gap-3 py-8 text-[#9A6B69]">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#EAD7D6] border-t-[#B87775]" />
              Loading current duration…
            </div>
          ) : (
            <div className="mt-2 space-y-5">
              {/* Hours stepper */}
              <div>
                <label className="mb-2 block text-[13px] font-medium text-[#8a5a58]">Hours</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Decrease hours"
                    onClick={() => setDuration((d) => ({ ...d, hours: Math.max(0, d.hours - 1) }))}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E3C9C8] text-lg text-[#B87775] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
                  >
                    −
                  </button>
                  <div className="flex-1 rounded-xl border border-[#E3C9C8] bg-white py-2 text-center font-serif text-2xl tabular-nums text-[#3a2e2c]">
                    {duration.hours}
                  </div>
                  <button
                    type="button"
                    aria-label="Increase hours"
                    onClick={() =>
                      setDuration((d) => ({ ...d, hours: Math.min(HOURS_MAX, d.hours + 1) }))
                    }
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E3C9C8] text-lg text-[#B87775] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Minutes pills */}
              <div>
                <label className="mb-2 block text-[13px] font-medium text-[#8a5a58]">Minutes</label>
                <div className="grid grid-cols-4 gap-2">
                  {MINUTE_STEPS.map((m) => {
                    const active = duration.minutes === m;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setDuration((d) => ({ ...d, minutes: m }))}
                        className={cn(
                          "h-11 rounded-xl border text-sm tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]",
                          active
                            ? "border-[#B87775] bg-[#B87775] text-white"
                            : "border-[#E3C9C8] bg-white text-[#8a5a58] hover:bg-[#FBEFEF]"
                        )}
                      >
                        {m.toString().padStart(2, "0")}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Live preview */}
              <div className="rounded-xl border border-[#EAD7D6] bg-[#FBEFEF]/40 px-4 py-3 text-sm text-[#8a5a58]">
                Each booking will reserve{" "}
                <span className="font-medium text-[#3a2e2c]">
                  {formatDuration(duration) ?? "0 min"}
                </span>
                .
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setIsDurationModalOpen(false)}
                  className="inline-flex h-11 items-center rounded-full border border-[#E3C9C8] px-5 text-sm font-medium text-[#8a5a58] transition hover:bg-[#FBEFEF]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveDuration}
                  disabled={isSavingDuration}
                  className="inline-flex h-11 items-center rounded-full bg-[#B87775] px-6 text-sm font-medium text-white transition hover:bg-[#a96764] disabled:opacity-60"
                >
                  {isSavingDuration ? "Saving…" : "Save duration"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
